import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { AppContext } from '../../App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { 
  FaArrowLeft, FaBox, FaTruck, FaCheck, FaClock, FaPaperclip, 
  FaPaperPlane, FaDownload, FaUser, FaStore, FaMapMarkerAlt, 
  FaCreditCard, FaFileInvoice, FaShippingFast, FaExternalLinkAlt
} from 'react-icons/fa';
import { formatPrice } from '../../utils/currency';

export default function UserOrderDetail() {
  const { orderId } = useParams();
  const { i18n } = useTranslation();
  const { API, user } = useContext(AppContext);
  const [order, setOrder] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchOrder();
    fetchMessages();
  }, [orderId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchOrder = async () => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}`);
      setOrder(response.data);
    } catch (error) {
      console.error('Error fetching order:', error);
      toast.error(i18n.language === 'fr' ? 'Erreur lors du chargement' : 'Error loading order');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}/messages`);
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() && files.length === 0) return;

    setSending(true);
    try {
      const formData = new FormData();
      formData.append('sender_id', user.id);
      formData.append('sender_name', user.name);
      formData.append('sender_role', 'customer');
      formData.append('content', newMessage);
      
      files.forEach(file => {
        formData.append('files', file);
      });

      await axios.post(`${API}/orders/${orderId}/messages`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setNewMessage('');
      setFiles([]);
      fetchMessages();
      toast.success(i18n.language === 'fr' ? 'Message envoyé' : 'Message sent');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error(i18n.language === 'fr' ? 'Erreur d\'envoi' : 'Error sending');
    } finally {
      setSending(false);
    }
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(prev => [...prev, ...selectedFiles]);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const getStatusInfo = (status) => {
    const statuses = {
      pending: { label: i18n.language === 'fr' ? 'En attente' : 'Pending', color: 'bg-orange-100 text-orange-700', icon: <FaClock /> },
      pending_payment: { label: i18n.language === 'fr' ? 'Paiement requis' : 'Payment required', color: 'bg-amber-100 text-amber-700', icon: <FaCreditCard /> },
      confirmed: { label: i18n.language === 'fr' ? 'Confirmée' : 'Confirmed', color: 'bg-blue-100 text-blue-700', icon: <FaCheck /> },
      shipped: { label: i18n.language === 'fr' ? 'Expédiée' : 'Shipped', color: 'bg-purple-100 text-purple-700', icon: <FaTruck /> },
      delivered: { label: i18n.language === 'fr' ? 'Livrée' : 'Delivered', color: 'bg-green-100 text-green-700', icon: <FaCheck /> }
    };
    return statuses[status] || statuses.pending;
  };

  const getPaymentLabel = (method) => {
    const methods = {
      cash: i18n.language === 'fr' ? 'Espèces' : 'Cash',
      card: i18n.language === 'fr' ? 'Carte bancaire' : 'Credit Card',
      bank_transfer: i18n.language === 'fr' ? 'Virement bancaire' : 'Bank Transfer',
      purchase_order: i18n.language === 'fr' ? 'Bon de commande' : 'Purchase Order',
      delivery: i18n.language === 'fr' ? 'Paiement à la livraison' : 'Payment on Delivery'
    };
    return methods[method] || method;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0A2540]"></div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <p>{i18n.language === 'fr' ? 'Commande introuvable' : 'Order not found'}</p>
      </div>
    );
  }

  const statusInfo = getStatusInfo(order.status);

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <Link to="/user/orders">
              <Button variant="outline" size="icon">
                <FaArrowLeft />
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-[#0A2540]">
                {i18n.language === 'fr' ? 'Commande' : 'Order'} #{order.id.substring(0, 8).toUpperCase()}
              </h1>
              <p className="text-sm text-gray-500">
                {new Date(order.created_at).toLocaleString(i18n.language === 'fr' ? 'fr-FR' : 'en-US')}
              </p>
            </div>
          </div>
          <span className={`px-4 py-2 rounded-full font-medium flex items-center gap-2 ${statusInfo.color}`}>
            {statusInfo.icon}
            {statusInfo.label}
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Order Details */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Tracking Info - Prominent display if shipped */}
            {order.tracking_number && (
              <Card className="bg-gradient-to-r from-purple-50 to-cyan-50 border-purple-200">
                <CardContent className="p-6">
                  <h2 className="text-xl font-bold mb-4 flex items-center gap-2 text-purple-800">
                    <FaShippingFast className="text-purple-600" />
                    {i18n.language === 'fr' ? '📦 Suivi de livraison' : '📦 Delivery Tracking'}
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <p className="text-sm text-gray-500 mb-1">
                        {i18n.language === 'fr' ? 'Transporteur' : 'Carrier'}
                      </p>
                      <p className="font-bold text-lg">{order.carrier || 'N/A'}</p>
                    </div>
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <p className="text-sm text-gray-500 mb-1">
                        {i18n.language === 'fr' ? 'Numéro de suivi' : 'Tracking Number'}
                      </p>
                      <p className="font-bold text-lg font-mono text-purple-700">{order.tracking_number}</p>
                    </div>
                  </div>
                  {order.shipped_at && (
                    <p className="text-sm text-gray-600 mt-4">
                      {i18n.language === 'fr' ? 'Expédié le' : 'Shipped on'}: {new Date(order.shipped_at).toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', {
                        day: 'numeric', month: 'long', year: 'numeric'
                      })}
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Order Items */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <FaBox className="text-[#0A2540]" />
                  {i18n.language === 'fr' ? 'Articles commandés' : 'Ordered Items'}
                </h2>
                <div className="space-y-4">
                  {order.items?.map((item, index) => (
                    <div key={index} className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium">{item.name || item.product_name}</p>
                        <p className="text-sm text-gray-500">
                          {i18n.language === 'fr' ? 'Quantité' : 'Qty'}: {item.quantity} • 
                          {item.type === 'new' ? (i18n.language === 'fr' ? ' Neuf' : ' New') :
                           item.type === 'used' ? (i18n.language === 'fr' ? ' Occasion' : ' Used') : ' Location'}
                        </p>
                      </div>
                      <p className="font-bold text-[#0A2540]">
                        {formatPrice((item.price || item.unit_price || item.total_price) * (item.quantity || 1), 'EUR')}
                      </p>
                    </div>
                  ))}
                </div>
                {order.delivery_cost > 0 && (
                  <div className="flex justify-between items-center p-4 border-t mt-4">
                    <span className="text-gray-600">{i18n.language === 'fr' ? 'Frais de livraison' : 'Delivery'}</span>
                    <span>{formatPrice(order.delivery_cost, 'EUR')}</span>
                  </div>
                )}
                <div className="border-t mt-4 pt-4">
                  <div className="flex justify-between text-lg font-bold">
                    <span>Total</span>
                    <span className="text-[#0A2540]">{formatPrice(order.total, 'EUR')}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quote reference */}
            {order.quote_id && (
              <Link to={`/user/proposals/${order.quote_id}`}>
                <Card className="hover:shadow-md transition-shadow cursor-pointer">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <FaFileInvoice className="text-[#635BFF] text-xl" />
                      <span>{i18n.language === 'fr' ? 'Voir le devis associé' : 'View associated quote'}</span>
                    </div>
                    <FaExternalLinkAlt className="text-gray-400" />
                  </CardContent>
                </Card>
              </Link>
            )}

            {/* Messages Section */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                  💬 {i18n.language === 'fr' ? 'Messages avec le fournisseur' : 'Messages with supplier'}
                </h2>
                
                {/* Messages List */}
                <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto mb-4">
                  {messages.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">
                      {i18n.language === 'fr' ? 'Aucun message. Contactez le fournisseur!' : 'No messages yet. Contact the supplier!'}
                    </p>
                  ) : (
                    <div className="space-y-4">
                      {messages.map(msg => (
                        <div 
                          key={msg.id} 
                          className={`flex ${msg.sender_role === 'customer' ? 'justify-end' : 'justify-start'}`}
                        >
                          <div className={`max-w-[80%] rounded-lg p-3 ${
                            msg.sender_role === 'customer' 
                              ? 'bg-[#635BFF] text-white' 
                              : 'bg-white border'
                          }`}>
                            <div className="flex items-center gap-2 mb-1">
                              {msg.sender_role === 'customer' ? <FaUser size={12} /> : <FaStore size={12} />}
                              <span className="text-xs font-medium">{msg.sender_name}</span>
                              <span className="text-xs opacity-70">
                                {new Date(msg.created_at).toLocaleTimeString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', {
                                  hour: '2-digit', minute: '2-digit'
                                })}
                              </span>
                            </div>
                            <p className="text-sm">{msg.content}</p>
                            {msg.attachments?.length > 0 && (
                              <div className="mt-2 space-y-1">
                                {msg.attachments.map(att => (
                                  <a 
                                    key={att.id}
                                    href={att.path.startsWith('/api/') 
                                      ? `${API.replace('/api', '')}${att.path}`
                                      : `${API}${att.path}`
                                    }
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`flex items-center gap-2 text-xs ${
                                      msg.sender_role === 'customer' ? 'text-blue-200 hover:text-white' : 'text-blue-600 hover:text-blue-800'
                                    }`}
                                  >
                                    <FaDownload />
                                    {att.filename}
                                  </a>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </div>
                  )}
                </div>

                {/* Message Input */}
                <form onSubmit={handleSendMessage} className="space-y-3">
                  {files.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {files.map((file, idx) => (
                        <div key={idx} className="flex items-center gap-2 bg-blue-50 px-3 py-1 rounded text-sm">
                          <FaPaperclip className="text-blue-500" />
                          <span className="truncate max-w-32">{file.name}</span>
                          <button type="button" onClick={() => removeFile(idx)} className="text-red-500 hover:text-red-700">×</button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      multiple
                      accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <FaPaperclip />
                    </Button>
                    <Textarea
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder={i18n.language === 'fr' ? 'Votre message...' : 'Your message...'}
                      className="flex-1 min-h-[60px]"
                    />
                    <Button type="submit" disabled={sending} className="bg-[#635BFF] hover:bg-[#5248d9]">
                      <FaPaperPlane />
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Info */}
          <div className="space-y-6">
            {/* Delivery Address */}
            <Card>
              <CardContent className="p-6">
                <h3 className="font-bold mb-4 flex items-center gap-2">
                  <FaMapMarkerAlt className="text-[#0A2540]" />
                  {i18n.language === 'fr' ? 'Adresse de livraison' : 'Delivery Address'}
                </h3>
                <p className="text-sm text-gray-600">{order.delivery_address}</p>
              </CardContent>
            </Card>

            {/* Payment Info */}
            <Card>
              <CardContent className="p-6">
                <h3 className="font-bold mb-4 flex items-center gap-2">
                  <FaCreditCard className="text-[#0A2540]" />
                  {i18n.language === 'fr' ? 'Paiement' : 'Payment'}
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">{i18n.language === 'fr' ? 'Méthode' : 'Method'}</span>
                    <span className="font-medium">{getPaymentLabel(order.payment_method)}</span>
                  </div>
                  {order.payment_status && (
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">{i18n.language === 'fr' ? 'Statut' : 'Status'}</span>
                      <Badge className={
                        order.payment_status === 'paid' ? 'bg-green-100 text-green-700' :
                        order.payment_status === 'invoiced' ? 'bg-blue-100 text-blue-700' :
                        'bg-orange-100 text-orange-700'
                      }>
                        {order.payment_status === 'paid' ? '✓ Payé' :
                         order.payment_status === 'invoiced' ? 'Facturé' : 'En attente'}
                      </Badge>
                    </div>
                  )}
                  {order.invoice_number && (
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">{i18n.language === 'fr' ? 'Facture' : 'Invoice'}</span>
                      <span className="font-mono text-sm">{order.invoice_number}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Order Timeline */}
            <Card>
              <CardContent className="p-6">
                <h3 className="font-bold mb-4">📋 {i18n.language === 'fr' ? 'Historique' : 'Timeline'}</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-[#635BFF]"></div>
                    <div>
                      <p className="font-medium">{i18n.language === 'fr' ? 'Commande créée' : 'Order created'}</p>
                      <p className="text-xs text-gray-500">
                        {new Date(order.created_at).toLocaleString(i18n.language === 'fr' ? 'fr-FR' : 'en-US')}
                      </p>
                    </div>
                  </div>
                  {order.confirmed_at && (
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                      <div>
                        <p className="font-medium">{i18n.language === 'fr' ? 'Confirmée' : 'Confirmed'}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(order.confirmed_at).toLocaleString(i18n.language === 'fr' ? 'fr-FR' : 'en-US')}
                        </p>
                      </div>
                    </div>
                  )}
                  {order.shipped_at && (
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                      <div>
                        <p className="font-medium">{i18n.language === 'fr' ? 'Expédiée' : 'Shipped'}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(order.shipped_at).toLocaleString(i18n.language === 'fr' ? 'fr-FR' : 'en-US')}
                        </p>
                      </div>
                    </div>
                  )}
                  {order.delivered_at && (
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full bg-green-500"></div>
                      <div>
                        <p className="font-medium">{i18n.language === 'fr' ? 'Livrée' : 'Delivered'}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(order.delivered_at).toLocaleString(i18n.language === 'fr' ? 'fr-FR' : 'en-US')}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
